import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import contractService from '../../services/contractService';
import countryService from '../../services/countryService';
import { useCountry } from '../../hooks/useCountry';
import { Button, Spinner, Alert, Badge } from '../ui';

const TemplateThumbnail = ({ url, title }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(0.252);

  useEffect(() => {
    if (!containerRef.current) return;
    const update = () => {
      const w = containerRef.current?.offsetWidth;
      if (w) setScale(w / 794);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', height: Math.round(260 * scale / 0.252) + 'px', overflow: 'hidden', background: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
      <iframe
        src={url}
        title={title}
        style={{ width: '794px', height: '1123px', border: 'none', transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none', position: 'absolute', top: 0, left: 0 }}
        loading="lazy"
      />
    </div>
  );
};

const ContractWizard = () => {
  const navigate = useNavigate();
  const { countryCode, country: countryConfig, theme } = useCountry();
  const { t, i18n } = useTranslation();
  const colors = theme?.colors || {};

  // Get country data and map to jurisdiction
  const jurisdiction = countryCode || 'usa';
  const [countryData, setCountryData] = useState(countryService.getCountryFromCache(jurisdiction));
  const state = jurisdiction === 'usa' ? 'california' : 'default';
  const [questionnaire, setQuestionnaire] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // List of enabled jurisdictions
  const enabledJurisdictions = ['usa', 'france', 'uk', 'germany', 'italy', 'canada', 'australia', 'spain'];

  // Theme-aware color helpers
  const primaryColor = colors.primary || '#2563eb';
  const activeStepBg = theme ? colors.buttonBg : undefined;
  const activeStepColor = theme ? colors.primary : '#2563eb';

  // Set language from country config
  useEffect(() => {
    if (countryConfig?.defaultLanguage && i18n.language !== countryConfig.defaultLanguage) {
      i18n.changeLanguage(countryConfig.defaultLanguage);
    }
  }, [countryConfig, i18n]);

  // Fetch country data from API
  useEffect(() => {
    const fetchCountryData = async () => {
      const data = await countryService.getCountryByCode(jurisdiction);
      if (data) {
        setCountryData(data);
      }
    };
    fetchCountryData();
  }, [jurisdiction]);

  useEffect(() => {
    if (enabledJurisdictions.includes(jurisdiction)) {
      loadQuestionnaire();
    } else {
      setLoading(false);
    }
  }, [jurisdiction, state]);

  // Auto-save draft
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      const timer = setTimeout(() => {
        localStorage.setItem(`contract_draft_${jurisdiction}`, JSON.stringify(formData));
        setLastSaved(new Date());
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [formData, jurisdiction]);

  // Load saved draft
  useEffect(() => {
    const savedDraft = localStorage.getItem(`contract_draft_${jurisdiction}`);
    if (savedDraft) {
      try {
        setFormData(JSON.parse(savedDraft));
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, [jurisdiction]);

  const getFirstOfNextMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  };

  const loadQuestionnaire = async () => {
    try {
      setLoading(true);
      const data = await contractService.getQuestionnaire(jurisdiction, state);
      setQuestionnaire(data);

      // Extract defaults from questionnaire fields and apply them to formData
      // (only for fields not already set by a saved draft)
      const savedDraft = localStorage.getItem(`contract_draft_${jurisdiction}`);
      const existingData = savedDraft ? JSON.parse(savedDraft) : {};

      const fieldDefaults = {};
      (data.steps || []).forEach(step => {
        (step.fields || []).forEach(field => {
          const key = field.name || field.id;
          if (key && field.default !== undefined && !(key in existingData)) {
            fieldDefaults[key] = field.default;
          }
        });
      });

      // Add computed defaults
      if (!existingData.lease_start_date) {
        fieldDefaults.lease_start_date = getFirstOfNextMonth();
      }

      if (Object.keys(fieldDefaults).length > 0) {
        setFormData(prev => ({ ...fieldDefaults, ...prev }));
      }

      setLoading(false);
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Unified conditional check — supports both "conditional_logic" and "conditional" keys
  const isFieldVisible = (field) => {
    const condition = field.conditional_logic || field.conditional;
    if (!condition) return true;
    const fieldValue = formData[condition.field];
    switch (condition.operator) {
      case 'eq': return fieldValue === condition.value;
      case 'ne': return fieldValue !== condition.value;
      case 'in': return (condition.value || []).includes(fieldValue);
      default: return condition.action !== 'show' || !!fieldValue;
    }
  };

  const validateStep = (step) => {
    const stepErrors = {};
    const currentStepData = questionnaire.steps[step];

    currentStepData.fields.forEach(field => {
      if (!isFieldVisible(field)) return;

      const key = field.name || field.id;
      if (!key) return;
      const value = formData[key];

      if (field.required && (value === undefined || value === null || value === '')) {
        stepErrors[key] = t('wizard.validation.required', { label: field.label });
        return;
      }

      if (value !== undefined && value !== null && value !== '') {
        // Built-in type validations
        switch (field.type) {
          case 'email':
            if (!/\S+@\S+\.\S+/.test(value)) stepErrors[key] = t('wizard.validation.invalidEmail');
            break;
          case 'number':
          case 'currency':
            if (isNaN(value)) { stepErrors[key] = t('wizard.validation.invalidNumber'); break; }
            if (field.min !== undefined && parseFloat(value) < field.min)
              stepErrors[key] = t('wizard.validation.minValue', { min: field.min });
            if (field.max !== undefined && parseFloat(value) > field.max)
              stepErrors[key] = t('wizard.validation.maxValue', { max: field.max });
            break;
          case 'date':
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) stepErrors[key] = t('wizard.validation.invalidDate');
            break;
        }

        // validations[] array from questionnaire JSON
        (field.validations || []).forEach(rule => {
          if (stepErrors[key]) return; // already has an error
          switch (rule.type) {
            case 'pattern':
              if (!new RegExp(rule.value).test(String(value)))
                stepErrors[key] = rule.message;
              break;
            case 'min':
              if (parseFloat(value) < rule.value) stepErrors[key] = rule.message;
              break;
            case 'max':
              if (parseFloat(value) > rule.value) stepErrors[key] = rule.message;
              break;
          }
        });
      }
    });

    return stepErrors;
  };

  const handleNext = () => {
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }

    if (currentStep < questionnaire.steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (stepIndex) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
      setErrors({});
    }
  };

  // Transform form data for API (same as before - keeping the existing logic)
  const serializeCustomArticles = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value
        .filter(a => a.content && a.content.trim())
        .map((a, i) => `${a.title ? a.title + '\n' : ''}${a.content.trim()}`)
        .join('\n\n');
    }
    return '';
  };

  const transformFormDataForAPI = () => {
    if (jurisdiction === 'france') {
      return {
        ...formData,
        surface_area: parseFloat(formData.surface_area) || 0,
        bedrooms: parseInt(formData.bedrooms) || 1,
        bathrooms: parseInt(formData.bathrooms) || 1,
        parking_spaces: parseInt(formData.parking_spaces) || 0,
        rent_payment_day: parseInt(formData.rent_payment_day) || 1,
        monthly_rent_excluding_charges: parseFloat(formData.monthly_rent_excluding_charges) || 0,
        monthly_charges: parseFloat(formData.monthly_charges) || 0,
        security_deposit: parseFloat(formData.security_deposit) || 0,
        utilities_included: Array.isArray(formData.utilities_included) ? formData.utilities_included : [],
        additional_terms: serializeCustomArticles(formData.additional_terms),
      };
    }

    if (jurisdiction === 'uk') {
      return {
        property: {
          address_line1: formData.property_address,
          address_line2: formData.property_address2 || '',
          city: formData.property_city,
          postal_code: formData.property_postcode,
          property_type: formData.property_type,
          bedrooms: parseInt(formData.bedrooms) || 1,
          bathrooms: parseFloat(formData.bathrooms) || 1.0,
          furnished: formData.furnished,
          epc_rating: formData.epc_rating || '',
        },
        landlord: {
          full_name: formData.landlord_name,
          email: formData.landlord_email,
          phone: formData.landlord_phone,
          address_line1: formData.landlord_address || '',
          city: formData.landlord_city || '',
          postal_code: formData.landlord_postcode || '',
          is_business: formData.landlord_type === 'company',
        },
        tenant: {
          full_name: formData.tenant_name,
          email: formData.tenant_email || '',
          phone: formData.tenant_phone || '',
        },
        region: formData.region,
        tenancy_type: formData.tenancy_type || 'ast',
        lease_start_date: formData.lease_start_date,
        lease_term: formData.lease_term,
        monthly_rent: parseFloat(formData.monthly_rent),
        security_deposit: parseFloat(formData.security_deposit) || null,
        rent_due_day: parseInt(formData.rent_due_day) || 1,
        deposit_scheme: formData.deposit_scheme || '',
        utilities_included: Array.isArray(formData.utilities_included) ? formData.utilities_included : [],
        pets_allowed: formData.pets_allowed || 'no',
        smoking_allowed: formData.smoking_allowed || 'no',
      };
    }

    if (jurisdiction === 'germany') {
      return {
        property: {
          address_line1: formData.property_address,
          address_line2: formData.property_unit || '',
          city: formData.property_city,
          postal_code: formData.property_postcode,
          property_type: formData.property_type,
          bedrooms: parseInt(formData.bedrooms) || 1,
          bathrooms: parseFloat(formData.bathrooms) || 1.0,
          square_footage: parseFloat(formData.living_area) || 0,
          furnished: formData.furnished === 'yes',
        },
        landlord: {
          full_name: formData.landlord_name,
          email: formData.landlord_email,
          phone: formData.landlord_phone,
          address_line1: formData.landlord_address || '',
          city: formData.landlord_city || '',
          postal_code: formData.landlord_postcode || '',
        },
        tenant: {
          full_name: formData.tenant_name,
          email: formData.tenant_email || '',
          phone: formData.tenant_phone || '',
        },
        federal_state: formData.federal_state,
        lease_start_date: formData.lease_start_date,
        lease_end_date: formData.lease_end_date || null,
        lease_type: formData.lease_end_date ? 'fixed_term' : 'indefinite',
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        nebenkosten: parseFloat(formData.nebenkosten) || 0,
        security_deposit: parseFloat(formData.security_deposit) || 0,
        rent_due_day: parseInt(formData.rent_due_day) || 3,
        currency: 'EUR',
        pets_allowed: formData.pets_allowed || 'no',
        utilities_included: Array.isArray(formData.utilities_included) ? formData.utilities_included : [],
      };
    }

    if (jurisdiction === 'italy') {
      return {
        property: {
          address_line1: formData.property_address,
          address_line2: formData.property_unit || '',
          city: formData.property_city,
          postal_code: formData.property_cap,
          state_province: formData.property_province,
          property_type: formData.property_type,
          bedrooms: parseInt(formData.bedrooms) || 1,
          bathrooms: parseFloat(formData.bathrooms) || 1.0,
          square_footage: parseFloat(formData.superficie) || 0,
          furnished: formData.furnished === 'yes',
        },
        landlord: {
          full_name: formData.landlord_name,
          email: formData.landlord_email,
          phone: formData.landlord_phone,
          address_line1: formData.landlord_address || '',
          fiscal_code: formData.landlord_codice_fiscale || '',
        },
        tenant: {
          full_name: formData.tenant_name,
          email: formData.tenant_email || '',
          phone: formData.tenant_phone || '',
          fiscal_code: formData.tenant_codice_fiscale || '',
        },
        contract_type: formData.contract_type || 'libero_4_4',
        lease_start_date: formData.lease_start_date,
        lease_end_date: formData.lease_end_date || null,
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        security_deposit: parseFloat(formData.security_deposit) || 0,
        rent_due_day: parseInt(formData.rent_due_day) || 5,
        currency: 'EUR',
        cedolare_secca: formData.cedolare_secca === 'yes',
        utilities_included: Array.isArray(formData.utilities_included) ? formData.utilities_included : [],
      };
    }

    if (jurisdiction === 'canada') {
      return {
        property: {
          address_line1: formData.property_address,
          address_line2: formData.property_unit || '',
          city: formData.property_city,
          state_province: formData.province,
          postal_code: formData.property_postal_code,
          property_type: formData.property_type,
          bedrooms: parseInt(formData.bedrooms) || 1,
          bathrooms: parseFloat(formData.bathrooms) || 1.0,
          furnished: formData.furnished === 'yes',
          parking_spaces: parseInt(formData.parking_spaces) || 0,
        },
        landlord: {
          full_name: formData.landlord_name,
          email: formData.landlord_email,
          phone: formData.landlord_phone,
          address_line1: formData.landlord_address || '',
        },
        tenant: {
          full_name: formData.tenant_name,
          email: formData.tenant_email || '',
          phone: formData.tenant_phone || '',
        },
        province: formData.province,
        lease_start_date: formData.lease_start_date,
        lease_end_date: formData.lease_end_date || null,
        lease_type: formData.lease_end_date ? 'fixed_term' : 'month_to_month',
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        security_deposit: parseFloat(formData.security_deposit) || 0,
        rent_due_day: parseInt(formData.rent_due_day) || 1,
        currency: 'CAD',
        pets_allowed: formData.pets_allowed || 'no',
        smoking_allowed: formData.smoking_allowed || 'no',
        utilities_included: Array.isArray(formData.utilities_included) ? formData.utilities_included : [],
      };
    }

    if (jurisdiction === 'australia') {
      return {
        property: {
          address_line1: formData.property_address,
          address_line2: formData.property_unit || '',
          city: formData.property_suburb,
          postal_code: formData.property_postcode,
          property_type: formData.property_type,
          bedrooms: parseInt(formData.bedrooms) || 1,
          bathrooms: parseFloat(formData.bathrooms) || 1.0,
          furnished: formData.furnished,
          parking_spaces: parseInt(formData.parking_spaces) || 0,
        },
        landlord: {
          full_name: formData.landlord_name,
          email: formData.landlord_email,
          phone: formData.landlord_phone,
          address_line1: formData.landlord_address || '',
          tfn: formData.landlord_abn || '',
        },
        tenant: {
          full_name: formData.tenant_name,
          email: formData.tenant_email || '',
          phone: formData.tenant_phone || '',
        },
        state: formData.state,
        lease_start_date: formData.lease_start_date,
        lease_end_date: formData.lease_end_date || null,
        lease_type: formData.tenancy_type === 'fixed_term' ? 'fixed_term' : 'periodic',
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        security_deposit: parseFloat(formData.bond_amount) || 0,
        rent_due_day: formData.rent_due_day || '1',
        currency: 'AUD',
        bond_lodgement_authority: formData.bond_lodgement_authority || '',
        pets_allowed: formData.pets_allowed || 'no',
        utilities_included: Array.isArray(formData.utilities_included) ? formData.utilities_included : [],
      };
    }

    if (jurisdiction === 'spain') {
      return {
        property: {
          address_line1: formData.property_address || formData['property.address_line1'],
          address_line2: formData.property_unit || '',
          city: formData.property_city || formData['property.city'],
          postal_code: formData.property_postal_code || formData['property.postal_code'],
          property_type: formData.property_type || formData['property.property_type'] || 'apartment',
          bedrooms: parseInt(formData.bedrooms || formData['property.bedrooms']) || 2,
          bathrooms: parseFloat(formData.bathrooms || formData['property.bathrooms']) || 1.0,
          square_footage: parseFloat(formData.square_footage || formData['property.square_footage']) || 0,
          furnished: (formData.furnished || formData['property.furnished']) === 'true',
          catastral_reference: formData.catastral_reference || formData['property.catastral_reference'] || '',
        },
        landlord: {
          full_name: formData.landlord_name || formData['landlord.full_name'],
          email: formData.landlord_email || formData['landlord.email'] || '',
          phone: formData.landlord_phone || formData['landlord.phone'] || '',
          address_line1: formData.landlord_address || formData['landlord.address_line1'] || '',
          nie_nif: formData.landlord_nie_nif || formData['landlord.nie_nif'] || '',
        },
        tenant: {
          full_name: formData.tenant_name || formData['tenant.full_name'],
          email: formData.tenant_email || formData['tenant.email'] || '',
          phone: formData.tenant_phone || formData['tenant.phone'] || '',
          nie_nif: formData.tenant_nie_nif || formData['tenant.nie_nif'] || '',
        },
        autonomous_community: formData.autonomous_community,
        contract_type: formData.contract_type || 'vivienda_habitual',
        lease_start_date: formData.lease_start_date,
        lease_end_date: formData.lease_end_date || null,
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        security_deposit: parseFloat(formData.security_deposit) || 0,
        fianza_months: parseInt(formData.fianza_months) || 1,
        additional_guarantee: parseFloat(formData.additional_guarantee) || null,
        rent_due_day: parseInt(formData.rent_due_day) || 1,
        rent_update_index: formData.rent_update_index || 'ipc',
        ibi_paid_by: formData.ibi_paid_by || 'landlord',
        community_fees_included: formData.community_fees_included === 'true',
        energy_certificate: formData.energy_certificate || '',
        currency: 'EUR',
        pets_allowed: formData.pets_allowed || 'no',
        notice_to_terminate_days: parseInt(formData.notice_to_terminate_days) || 30,
        utilities_included: Array.isArray(formData.utilities_included) ? formData.utilities_included : [],
        additional_terms: formData.additional_terms || '',
      };
    }

    // USA default
    return {
      property: {
        address_line1: formData.property_address,
        address_line2: formData.property_unit || '',
        city: formData.property_city,
        state_province: 'CA',
        postal_code: formData.property_zip,
        country: 'USA',
        property_type: formData.property_type,
        bedrooms: parseInt(formData.bedrooms) || 1,
        bathrooms: parseFloat(formData.bathrooms) || 1.0,
        furnished: formData.furnished === 'yes',
        parking_spaces: parseInt(formData.parking_spaces) || 0,
        amenities: [],
        unit_number: formData.property_unit || '',
        floor_number: null,
        hoa_name: '',
        hoa_fee: null
      },
      landlord: {
        party_type: 'landlord',
        is_business: formData.landlord_type === 'business',
        full_name: formData.landlord_name,
        business_registration: '',
        email: formData.landlord_email,
        phone: formData.landlord_phone,
        address_line1: formData.landlord_address,
        address_line2: '',
        city: formData.landlord_city,
        state_province: formData.landlord_state || 'CA',
        postal_code: formData.landlord_zip,
        country: 'USA',
        ssn_ein: ''
      },
      tenant: {
        party_type: 'tenant',
        is_business: false,
        full_name: formData.tenant_name,
        email: formData.tenant_email || '',
        phone: formData.tenant_phone || '',
        address_line1: formData.property_address,
        address_line2: '',
        city: formData.property_city,
        state_province: formData.property_state,
        postal_code: formData.property_zip,
        country: 'USA',
        ssn: ''
      },
      lease_start_date: formData.lease_start_date,
      lease_end_date: formData.lease_end_date || null,
      lease_type: formData.lease_end_date ? 'fixed_term' : 'month_to_month',
      monthly_rent: parseFloat(formData.monthly_rent),
      security_deposit: parseFloat(formData.security_deposit) || null,
      rent_due_day: parseInt(formData.rent_due_day) || 1,
      currency: 'USD',
      state: 'CA',
      lead_paint_disclosure: formData.built_before_1978 === 'yes',
      lead_paint_present: formData.lead_paint_known === 'yes' ? true : false,
      rent_control_applies: formData.rent_control === 'yes',
      military_base_proximity: formData.military_base_nearby === 'yes',
      notice_to_terminate_days: 30,
      pets_allowed: formData.pets_allowed || 'no',
      pet_deposit: formData.pet_deposit ? parseFloat(formData.pet_deposit) : null,
      smoking_allowed: formData.smoking_allowed || 'no',
      utilities_included: Array.isArray(formData.utilities_included) ? formData.utilities_included : [],
      additional_terms: formData.additional_terms || ''
    };
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const apiData = transformFormDataForAPI();
      const result = await contractService.createContract(jurisdiction, apiData);

      // Clear saved draft on success
      localStorage.removeItem(`contract_draft_${jurisdiction}`);

      navigate(`/contracts/${result.contract.id}/preview`);
    } catch (error) {
      console.error('Failed to create contract:', error);
      const backendErrors = error.response?.data?.errors || {};
      const errorMessage = Object.keys(backendErrors).length > 0
        ? `Validation errors: ${Object.entries(backendErrors).map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`).join('; ')}`
        : t('wizard.submitError');
      setErrors({ submit: errorMessage });
      setSubmitting(false);
    }
  };

  // Summary data for sidebar
  const summaryData = useMemo(() => {
    const data = [];
    if (formData.property_address) data.push({ label: t('wizard.summary.property'), value: formData.property_address });
    if (formData.landlord_name) data.push({ label: t('wizard.summary.landlord'), value: formData.landlord_name });
    if (formData.tenant_name) data.push({ label: t('wizard.summary.tenant'), value: formData.tenant_name });
    const rentValue = formData.monthly_rent || formData.monthly_rent_excluding_charges;
    if (rentValue) {
      const symbol = countryData?.currency === 'EUR' ? '€' : countryData?.currency === 'GBP' ? '£' : '$';
      data.push({ label: t('wizard.summary.monthlyRent'), value: `${symbol}${rentValue}` });
    }
    if (formData.lease_start_date) data.push({ label: t('wizard.summary.startDate'), value: formData.lease_start_date });
    return data;
  }, [formData, countryData, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primaryLight}05 100%)` : undefined }}>
        <Spinner.Page message={t('wizard.loadingQuestionnaire')} />
      </div>
    );
  }

  // Show "coming soon" for non-enabled countries
  if (!enabledJurisdictions.includes(jurisdiction)) {
    return (
      <div className="min-h-screen py-12" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primaryLight}05 100%)` : undefined }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto animate-float" style={{ background: colors.buttonBg || 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {countryData?.name} {t('contracts.types.residential')}
            </h1>
            <Alert type="info" title={t('wizard.comingSoonTitle')} className="text-left mb-6">
              {t('wizard.comingSoonText', { country: countryData?.name })}
            </Alert>
            <div className="flex gap-4 justify-center">
              <Button variant="secondary" onClick={() => navigate('/')}>
                {t('common.back')}
              </Button>
              <Button onClick={() => { window.location.href = `${window.location.protocol}//us.rentydoc.com/contracts/create`; }}>
                {t('wizard.tryUsa')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!questionnaire || !questionnaire.steps || questionnaire.steps.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primaryLight}05 100%)` : undefined }}>
        <Alert type="error" title={t('common.error')}>
          {t('wizard.failedToLoad')}
        </Alert>
      </div>
    );
  }

  const currentStepData = questionnaire.steps[currentStep] || questionnaire.steps[0];
  const progress = ((currentStep + 1) / questionnaire.steps.length) * 100;

  return (
    <div className="min-h-screen pb-24 md:pb-0" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primaryLight}05 100%)` : undefined }}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">{questionnaire.title}</h1>
                </div>
                <p className="text-sm text-gray-500">{questionnaire.description}</p>
              </div>
            </div>

            {/* Save indicator */}
            <div className="flex items-center gap-4">
              {lastSaved && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('wizard.draftSaved')}
                </div>
              )}
              {currentStep === questionnaire.steps.length - 1 && (
                <Badge status="info" size="sm">
                  {countryData?.currency || 'USD'} {questionnaire.metadata?.price_eur || questionnaire.metadata?.price_usd || countryData?.price || '29.99'}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Main Content */}
          <div className="flex-1">
            {/* Progress Stepper */}
            <div className="bg-white rounded-2xl shadow-soft p-4 md:p-6 mb-6">
              {/* Desktop Stepper */}
              <div className="hidden md:flex items-center justify-between mb-4">
                {questionnaire.steps.map((step, index) => (
                  <React.Fragment key={step.id || index}>
                    <button
                      onClick={() => handleStepClick(index)}
                      className={`flex flex-col items-center group ${index <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                      disabled={index > currentStep}
                    >
                      <div
                        className={`
                          w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                          transition-all duration-300
                          ${index < currentStep
                            ? 'bg-emerald-500 text-white'
                            : index === currentStep
                              ? 'text-white ring-4'
                              : 'bg-gray-100 text-gray-400'
                          }
                          ${index <= currentStep ? 'group-hover:scale-110' : ''}
                        `}
                        style={index === currentStep ? {
                          background: activeStepBg || '#2563eb',
                          ringColor: `${activeStepColor}25`,
                          boxShadow: `0 0 0 4px ${activeStepColor}25`
                        } : undefined}
                      >
                        {index < currentStep ? (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span
                        className={`mt-2 text-xs font-medium max-w-[80px] text-center truncate ${
                          index <= currentStep ? 'text-gray-900' : 'text-gray-400'
                        }`}
                      >
                        {step.title}
                      </span>
                    </button>
                    {index < questionnaire.steps.length - 1 && (
                      <div
                        className={`flex-1 h-1 mx-2 rounded-full transition-all duration-500 ${
                          index < currentStep ? 'bg-emerald-500' : 'bg-gray-100'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile Stepper - Compact circles only */}
              <div className="md:hidden">
                <div className="flex items-center justify-center gap-2 mb-3">
                  {questionnaire.steps.map((step, index) => (
                    <button
                      key={step.id || index}
                      onClick={() => handleStepClick(index)}
                      disabled={index > currentStep}
                      className={`
                        w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs
                        transition-all duration-300
                        ${index < currentStep
                          ? 'bg-emerald-500 text-white'
                          : index === currentStep
                            ? 'text-white scale-110'
                            : 'bg-gray-100 text-gray-400'
                        }
                      `}
                      style={index === currentStep ? {
                        background: activeStepBg || '#2563eb',
                        boxShadow: `0 0 0 2px ${activeStepColor}25`
                      } : undefined}
                    >
                      {index < currentStep ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </button>
                  ))}
                </div>
                {/* Current step title on mobile */}
                <p className="text-center text-sm font-medium text-gray-900">
                  {currentStepData.title}
                </p>
              </div>

              <div className="hidden md:flex justify-between text-sm text-gray-500">
                <span>{t('wizard.stepOf', { current: currentStep + 1, total: questionnaire.steps.length })}</span>
                <span>{Math.round(progress)}% {t('wizard.complete')}</span>
              </div>
            </div>

            {/* Current Step Form */}
            <div className="bg-white rounded-2xl shadow-soft p-8 animate-fade-in">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{currentStepData.title}</h2>
              {currentStepData.description && (
                <p className="text-gray-500 mb-6">{currentStepData.description}</p>
              )}

              {errors.submit && (
                <Alert type="error" className="mb-6" dismissible onDismiss={() => setErrors({ ...errors, submit: null })}>
                  {errors.submit}
                </Alert>
              )}

              <div className="space-y-6">
                {currentStepData.fields.map(field => {
                  if (!isFieldVisible(field)) return null;

                  const fieldKey = field.name || field.id;
                  return (
                    <div key={fieldKey} className="group">
                      {field.type !== 'template_selector' && field.type !== 'custom_articles' && (
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {field.label}
                          {field.required && <span className="text-rose-500 ml-1">*</span>}
                        </label>
                      )}

                      {field.type === 'custom_articles' && (
                        <div className="mb-3">
                          <p className="text-sm font-medium text-gray-700">{field.label}</p>
                          {field.help_text && <p className="text-xs text-gray-400 mt-1">{field.help_text}</p>}
                        </div>
                      )}

                      {field.help_text && field.type !== 'template_selector' && field.type !== 'custom_articles' && (
                        <p className="text-sm text-gray-500 mb-2">{field.help_text}</p>
                      )}

                      {/* Text Input */}
                      {field.type === 'text' && (
                        <input
                          type="text"
                          value={formData[fieldKey] || ''}
                          onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                          placeholder={field.placeholder}
                          className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-0 ${
                            errors[fieldKey] ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onFocus={(e) => { e.target.style.borderColor = primaryColor; }}
                          onBlur={(e) => { if (!errors[fieldKey]) e.target.style.borderColor = ''; }}
                        />
                      )}

                      {/* Email Input */}
                      {field.type === 'email' && (
                        <div className="relative">
                          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                          </svg>
                          <input
                            type="email"
                            value={formData[fieldKey] || ''}
                            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                            placeholder={field.placeholder}
                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-0 ${
                              errors[fieldKey] ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onFocus={(e) => { e.target.style.borderColor = primaryColor; }}
                            onBlur={(e) => { if (!errors[fieldKey]) e.target.style.borderColor = ''; }}
                          />
                        </div>
                      )}

                      {/* Phone Input */}
                      {(field.type === 'tel' || field.type === 'phone') && (
                        <div className="relative">
                          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <input
                            type="tel"
                            value={formData[fieldKey] || ''}
                            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                            placeholder={field.placeholder}
                            className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-0 ${
                              errors[fieldKey] ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onFocus={(e) => { e.target.style.borderColor = primaryColor; }}
                            onBlur={(e) => { if (!errors[fieldKey]) e.target.style.borderColor = ''; }}
                          />
                        </div>
                      )}

                      {/* Number/Currency Input */}
                      {(field.type === 'number' || field.type === 'currency') && (
                        <div className="relative">
                          {field.type === 'currency' && (
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                              {countryData?.currency === 'EUR' ? '€' : countryData?.currency === 'GBP' ? '£' : '$'}
                            </span>
                          )}
                          <input
                            type="number"
                            value={formData[fieldKey] ?? ''}
                            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                            placeholder={field.placeholder}
                            min={field.min}
                            max={field.max}
                            step={field.step || (field.type === 'currency' ? '0.01' : 'any')}
                            className={`w-full ${field.type === 'currency' ? 'pl-10' : 'pl-4'} pr-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-0 ${
                              errors[fieldKey] ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onFocus={(e) => { e.target.style.borderColor = primaryColor; }}
                            onBlur={(e) => { if (!errors[fieldKey]) e.target.style.borderColor = ''; }}
                          />
                        </div>
                      )}

                      {/* Date Input */}
                      {field.type === 'date' && (
                        <input
                          type="date"
                          value={formData[fieldKey] || ''}
                          onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                          className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-0 ${
                            errors[fieldKey] ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onFocus={(e) => { e.target.style.borderColor = primaryColor; }}
                          onBlur={(e) => { if (!errors[fieldKey]) e.target.style.borderColor = ''; }}
                        />
                      )}

                      {/* Select */}
                      {field.type === 'select' && (
                        <select
                          value={formData[fieldKey] || ''}
                          onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                          className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-0 appearance-none bg-white ${
                            errors[fieldKey] ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onFocus={(e) => { e.target.style.borderColor = primaryColor; }}
                          onBlur={(e) => { if (!errors[fieldKey]) e.target.style.borderColor = ''; }}
                        >
                          <option value="">{t('wizard.select')}</option>
                          {(field.options || []).map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}

                      {/* Radio */}
                      {field.type === 'radio' && (
                        <div className="grid grid-cols-2 gap-3">
                          {(field.options || []).map(option => (
                            <label
                              key={option.value}
                              className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                                formData[fieldKey] === option.value
                                  ? 'border-gray-300'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={formData[fieldKey] === option.value ? {
                                borderColor: primaryColor,
                                backgroundColor: `${primaryColor}08`
                              } : undefined}
                            >
                              <input
                                type="radio"
                                name={fieldKey}
                                value={option.value}
                                checked={formData[fieldKey] === option.value}
                                onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                className="w-4 h-4"
                                style={{ accentColor: primaryColor }}
                              />
                              <span className="text-gray-700">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {/* Checkbox */}
                      {field.type === 'checkbox' && (
                        <label className="flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 hover:border-gray-300">
                          <input
                            type="checkbox"
                            checked={formData[fieldKey] === true || formData[fieldKey] === 'true'}
                            onChange={(e) => handleFieldChange(fieldKey, e.target.checked)}
                            className="w-5 h-5 rounded"
                            style={{ accentColor: primaryColor }}
                          />
                          <span className="text-gray-700">{field.label}</span>
                        </label>
                      )}

                      {/* Checkbox Group */}
                      {(field.type === 'checkbox_group' || field.type === 'multiselect') && (
                        <div className="grid grid-cols-2 gap-3">
                          {(field.options || []).map(option => (
                            <label
                              key={option.value}
                              className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                                (formData[fieldKey] || []).includes(option.value)
                                  ? 'border-gray-300'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={(formData[fieldKey] || []).includes(option.value) ? {
                                borderColor: primaryColor,
                                backgroundColor: `${primaryColor}08`
                              } : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={(formData[fieldKey] || []).includes(option.value)}
                                onChange={(e) => {
                                  const currentValues = formData[fieldKey] || [];
                                  const newValues = e.target.checked
                                    ? [...currentValues, option.value]
                                    : currentValues.filter(v => v !== option.value);
                                  handleFieldChange(fieldKey, newValues);
                                }}
                                className="w-5 h-5 rounded"
                                style={{ accentColor: primaryColor }}
                              />
                              <span className="text-gray-700">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {/* Textarea */}
                      {field.type === 'textarea' && (
                        <textarea
                          value={formData[fieldKey] || ''}
                          onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                          placeholder={field.placeholder}
                          rows={field.rows || 4}
                          className={`w-full px-4 py-3 border-2 rounded-xl transition-all duration-200 focus:ring-0 resize-none ${
                            errors[fieldKey] ? 'border-rose-300 bg-rose-50' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onFocus={(e) => { e.target.style.borderColor = primaryColor; }}
                          onBlur={(e) => { if (!errors[fieldKey]) e.target.style.borderColor = ''; }}
                        />
                      )}

                      {/* Info Box */}
                      {field.type === 'info' && (
                        <div className="border-l-4 p-4 rounded-xl" style={{ backgroundColor: `${primaryColor}08`, borderColor: primaryColor }}>
                          <p style={{ color: `${primaryColor}dd` }}>{field.content || field.help_text}</p>
                          {field.legal_info && (
                            <p className="text-xs mt-2 italic" style={{ color: `${primaryColor}99` }}>{field.legal_info}</p>
                          )}
                        </div>
                      )}

                      {/* Template Selector — real PDF thumbnails */}
                      {field.type === 'template_selector' && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                          {(field.options || []).map(opt => {
                            const selected = (formData[fieldKey] || 'classic') === opt.value;
                            const thumbnailUrl = `/api/v1/${jurisdiction}/templates/${opt.value}/thumbnail/`;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => handleFieldChange(fieldKey, opt.value)}
                                className="relative rounded-2xl text-left transition-all duration-200 focus:outline-none overflow-hidden"
                                style={{
                                  border: selected ? `2.5px solid ${primaryColor}` : '2px solid #e5e7eb',
                                  boxShadow: selected ? `0 0 0 4px ${primaryColor}18, 0 8px 24px ${primaryColor}22` : '0 2px 8px rgba(0,0,0,0.06)',
                                  background: 'white',
                                }}
                              >
                                {/* Real PDF thumbnail — responsive iframe */}
                                <div style={{ position: 'relative' }}>
                                  <TemplateThumbnail url={thumbnailUrl} title={`Aperçu ${opt.label}`} />
                                  {selected && (
                                    <div className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center shadow-lg z-10" style={{ background: primaryColor }}>
                                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/></svg>
                                    </div>
                                  )}
                                </div>
                                {/* Label row */}
                                <div className="p-3">
                                  <div className="flex items-center justify-between mb-0.5">
                                    <span className="font-semibold text-sm text-gray-900">{opt.label}</span>
                                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: selected ? `${primaryColor}15` : '#f3f4f6', color: selected ? primaryColor : '#9ca3af', fontWeight: 500 }}>
                                      {selected ? '✓ Sélectionné' : 'Choisir'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-400 leading-snug">{opt.description}</p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Custom Articles Editor */}
                      {field.type === 'custom_articles' && (() => {
                        const articles = Array.isArray(formData[fieldKey]) ? formData[fieldKey] : [];
                        const setArticles = (newArr) => handleFieldChange(fieldKey, newArr);
                        const addArticle = () => setArticles([...articles, { title: '', content: '' }]);
                        const removeArticle = (i) => setArticles(articles.filter((_, idx) => idx !== i));
                        const updateArticle = (i, key, val) => {
                          const updated = articles.map((a, idx) => idx === i ? { ...a, [key]: val } : a);
                          setArticles(updated);
                        };
                        const baseArticleNum = 10; // custom articles start at Art. 10
                        return (
                          <div className="space-y-3">
                            {articles.length === 0 && (
                              <div className="rounded-xl border-2 border-dashed border-gray-200 p-6 text-center">
                                <svg className="w-8 h-8 text-gray-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-sm text-gray-400 mb-1">{t('wizard.noCustomClauses', 'Aucune clause particulière')}</p>
                                <p className="text-xs text-gray-400">{t('wizard.clausesOptional', 'Optionnel — la plupart des baux n\'en nécessitent pas')}</p>
                              </div>
                            )}
                            {articles.map((article, i) => (
                              <div key={i} className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                                <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                                  <span className="text-xs font-bold px-2 py-0.5 rounded-md text-white" style={{ background: primaryColor }}>
                                    Art. {baseArticleNum + i}
                                  </span>
                                  <input
                                    type="text"
                                    value={article.title}
                                    onChange={e => updateArticle(i, 'title', e.target.value)}
                                    placeholder={field.placeholder_title || 'Titre de la clause (optionnel)'}
                                    className="flex-1 text-sm font-medium bg-transparent border-none outline-none text-gray-700 placeholder-gray-400"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => removeArticle(i)}
                                    className="text-gray-400 hover:text-rose-500 transition-colors p-1 rounded-lg hover:bg-rose-50"
                                  >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                                <textarea
                                  value={article.content}
                                  onChange={e => updateArticle(i, 'content', e.target.value)}
                                  placeholder={field.placeholder_content || 'Contenu de la clause…'}
                                  rows={3}
                                  className="w-full px-4 py-3 text-sm text-gray-700 border-none outline-none resize-none placeholder-gray-400"
                                  onFocus={e => { e.target.style.background = `${primaryColor}04`; }}
                                  onBlur={e => { e.target.style.background = ''; }}
                                />
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={addArticle}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed text-sm font-medium transition-all duration-200 hover:shadow-sm"
                              style={{ borderColor: `${primaryColor}50`, color: primaryColor, background: `${primaryColor}04` }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = primaryColor; e.currentTarget.style.background = `${primaryColor}08`; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = `${primaryColor}50`; e.currentTarget.style.background = `${primaryColor}04`; }}
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              {t('wizard.addClause', 'Ajouter une clause particulière')}
                            </button>
                          </div>
                        );
                      })()}

                      {/* Error Message */}
                      {errors[fieldKey] && (
                        <p className="mt-2 text-sm text-rose-600 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {errors[fieldKey]}
                        </p>
                      )}

                      {/* Legal Reference */}
                      {field.legal_reference && (
                        <p className="mt-2 text-xs text-gray-500 italic">
                          {t('wizard.legalReference')}: {field.legal_reference}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Navigation Buttons - Desktop */}
              <div className="hidden md:flex justify-between mt-10 pt-6 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  icon={({ className }) => (
                    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  )}
                >
                  {t('common.back')}
                </Button>

                <Button
                  onClick={handleNext}
                  loading={submitting}
                  icon={currentStep === questionnaire.steps.length - 1 ? undefined : ({ className }) => (
                    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  iconPosition="right"
                  style={theme ? { background: colors.buttonBg } : undefined}
                >
                  {currentStep === questionnaire.steps.length - 1 ? t('wizard.createContract') : t('common.next')}
                </Button>
              </div>
            </div>
          </div>

          {/* Summary Sidebar - only on last step */}
          {currentStep === questionnaire.steps.length - 1 && <div className="hidden lg:block w-80">
            <div className="bg-white rounded-2xl shadow-soft p-6 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">{t('wizard.contractSummary')}</h3>

              {summaryData.length > 0 ? (
                <div className="space-y-4">
                  {summaryData.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span className="text-gray-500">{item.label}</span>
                      <span className="font-medium text-gray-900 text-right max-w-[140px] truncate">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">
                  {t('wizard.summaryEmpty')}
                </p>
              )}

              {/* Price Card */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="rounded-xl p-4" style={{ background: theme ? `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.primaryLight}15 100%)` : 'linear-gradient(135deg, #eff6ff 0%, #eef2ff 100%)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">{t('wizard.documentPrice')}</span>
                    <Badge status="success" size="sm">{t('wizard.bestValue')}</Badge>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {countryData?.currency === 'EUR' ? '€' : countryData?.currency === 'GBP' ? '£' : '$'}
                    {questionnaire.metadata?.price_eur || questionnaire.metadata?.price_usd || countryData?.price || '29.99'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{t('wizard.oneTimePayment')}</p>
                </div>
              </div>

              {/* Features */}
              <div className="mt-6 space-y-3">
                {[
                  t('wizard.features.legallyCompliant'),
                  t('wizard.features.instantPdf'),
                  t('wizard.features.editableTemplate'),
                  t('wizard.features.freeUpdates')
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </div>}
        </div>
      </div>

      {/* Mobile Sticky Navigation Bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-40 shadow-lg">
        {/* Progress indicator */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-500">
            {t('wizard.stepOf', { current: currentStep + 1, total: questionnaire.steps.length })}
          </span>
          <div className="flex-1 mx-3 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, background: activeStepBg || '#2563eb' }}
            />
          </div>
          <span className="text-xs font-medium" style={{ color: primaryColor }}>{Math.round(progress)}%</span>
        </div>

        {/* Navigation buttons */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex-1"
            size="lg"
          >
            <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </Button>

          <Button
            onClick={handleNext}
            loading={submitting}
            className="flex-[2]"
            size="lg"
            style={theme ? { background: colors.buttonBg } : undefined}
          >
            {currentStep === questionnaire.steps.length - 1 ? (
              t('wizard.createContract')
            ) : (
              <>
                {t('common.next')}
                <svg className="w-5 h-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContractWizard;
